import { createSelector } from 'reselect';
import {
  createLayoutSegmentByIdSelector,
  getVideoLayoutInfoSelector,
  getVideoSelector,
} from 'redux/modules/embed/selectors';
import { State } from 'redux/types';
import { MINIMUM_SLIDE_DURATION_MILLIS } from 'utils/constants';
import {
  getLayoutSegmentDisplayName,
  getLayoutTimelineDurationMillis,
  getLayoutTimelineOriginMillis,
} from 'utils/embed/layout';

export const createSegmentIdByIndexSelector = (
  videoId: string,
  index: number,
) => (state: State) => {
  const layoutTimeline = getVideoLayoutInfoSelector(videoId)(state);
  return layoutTimeline?.[index]?.segmentId;
};

export const createFramingOriginMillisSelector = (videoId: string) => (
  state: State,
) => {
  const layoutTimeline = getVideoLayoutInfoSelector(videoId)(state);
  return getLayoutTimelineOriginMillis(layoutTimeline);
};

export const createLayoutTimelineOffsetMillisSelector = (videoId: string) => (
  state: State,
) => {
  const video = getVideoSelector(videoId)(state);

  const layoutStartMillis = createFramingOriginMillisSelector(videoId)(state);
  const videoStartMillis = video?.playFromMillis ?? 0;

  return layoutStartMillis - videoStartMillis;
};

export const createSegmentsAroundGapSelector = (
  videoId: string,
  gapIndex: number,
) => (state: State) => {
  const layoutTimeline = getVideoLayoutInfoSelector(videoId)(state);
  return [layoutTimeline[gapIndex - 1], layoutTimeline[gapIndex]];
};

export const createLayoutTimelineSegmentIdsSelector = (videoId: string) =>
  createSelector([getVideoLayoutInfoSelector(videoId)], layoutTimeline =>
    layoutTimeline?.map(segment => segment.segmentId),
  );

export const createLayoutTimelineDurationMillisSelector = (videoId: string) =>
  createSelector([getVideoLayoutInfoSelector(videoId)], layoutTimeline =>
    getLayoutTimelineDurationMillis(layoutTimeline),
  );

export const createSegmentDisplayNameSelector = (
  videoId: string,
  segmentId: string,
) => (state: State) => {
  const segment = createLayoutSegmentByIdSelector(videoId, segmentId)(state);
  return getLayoutSegmentDisplayName(segment, { truncate: true });
};

// layout handles are positioned relative to a container which is placed in the
// correct location. in other words, the first handle will always have position 0.
export const createLayoutTimelineHandlePositionMillisSelector = (
  videoId: string,
  gapIndex: number,
) =>
  createSelector(
    [createSegmentsAroundGapSelector(videoId, gapIndex)],
    (segments = []) => {
      const [prevSegment, nextSegment] = segments;

      if (!prevSegment && !nextSegment) {
        return undefined;
      }

      if (!nextSegment) {
        return prevSegment.playOffsetMillis + prevSegment.durationMillis;
      }

      return nextSegment.playOffsetMillis;
    },
  );

export const createDragHandleBoundsSelector = (
  videoId: string,
  gapIndex: number,
) => (state: State) => {
  const video = getVideoSelector(videoId)(state);
  const [prevSegment, nextSegment] = createSegmentsAroundGapSelector(
    videoId,
    gapIndex,
  )(state);

  const videoDurationMillis = video?.endMillis - video?.startMillis;

  const minMillis = !prevSegment
    ? video.playFromMillis
    : prevSegment.playOffsetMillis + MINIMUM_SLIDE_DURATION_MILLIS;

  const maxMillis = !nextSegment
    ? video.playFromMillis + videoDurationMillis
    : nextSegment.playOffsetMillis +
      nextSegment.durationMillis -
      MINIMUM_SLIDE_DURATION_MILLIS;

  return {
    minMillis,
    maxMillis,
  };
};
